import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Users } from "lucide-react";
import { ApiError } from "@/lib/api";
import { confirmAsync, pushToast } from "@/components/Toaster";
import { useCurrentUser, type Role } from "@/lib/auth";
import {
  type ResetLinkResponse,
  type TeamMember,
  useInviteTeamMember,
  useRemoveTeamMember,
  useTeam,
  useIssueResetLink,
  useResetMember2fa,
  useUpdateTeamMember,
} from "@/lib/team";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { SimpleSelect } from "@/components/ui/select";
import { HelpSection } from "@/components/HelpSection";
import { formatRelative, formatDateTime } from "@/lib/format";

const COMPANY_ROLES: Role[] = ["company_admin", "company_operator", "company_viewer"];

/**
 * TeamPage — /team
 *
 * Fixes applied:
 * - is_active toggle (Block/Unblock) — was missing in old UI
 * - Self-demotion prevented (own role is read-only)
 * - StatusBadge for pending/active/disabled
 * - Member edit drawer/dialog with role + is_active
 * - Invite: single unified flow (form → link), copy with toast feedback
 * - EmptyState with CTA when no members
 */
export function TeamPage() {
  const { t } = useTranslation();
  const team = useTeam();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState<{
    url: string;
    expires: string;
  } | null>(null);
  const [editTarget, setEditTarget] = useState<TeamMember | null>(null);

  const data = team.data ?? [];

  return (
    <section className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        title={t("team.title")}
        subtitle={t("team.subtitle")}
        actions={
          <Button
            onClick={() => setShowInvite(true)}
            iconLeft={<UserPlus size={16} />}
          >
            {t("team.invite")}
          </Button>
        }
      />

      {team.isLoading && (
        <p className="text-fg-muted text-sm">{t("team.loading")}</p>
      )}
      {team.isError && (
        <Alert tone="danger">{t("team.load_error")}</Alert>
      )}

      {!team.isLoading && !team.isError && data.length === 0 && (
        <EmptyState
          icon={Users}
          title={t("team.empty_title")}
          description={t("team.empty_description")}
          action={
            <Button
              onClick={() => setShowInvite(true)}
              iconLeft={<UserPlus size={16} />}
            >
              {t("team.invite")}
            </Button>
          }
        />
      )}

      {data.length > 0 && (
        <div className="max-w-4xl overflow-x-auto rounded-xl border border-border">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="bg-bg-secondary text-left text-fg-muted border-b border-border">
              <tr>
                <th className="px-4 py-3 font-medium">{t("team.col.username")}</th>
                <th className="px-4 py-3 font-medium">{t("team.col.role")}</th>
                <th className="px-4 py-3 font-medium">{t("team.col.status")}</th>
                <th className="px-4 py-3 font-medium">{t("team.col.last_login")}</th>
                <th className="px-4 py-3 font-medium text-right">
                  {t("team.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  onEdit={() => setEditTarget(m)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={(inv) => {
            setShowInvite(false);
            setInviteResult({ url: inv.invite_url, expires: inv.invite_expires_at });
          }}
        />
      )}

      {inviteResult && (
        <InviteLinkModal
          url={inviteResult.url}
          expiresAt={inviteResult.expires}
          onClose={() => setInviteResult(null)}
        />
      )}

      {editTarget && (
        <EditMemberDialog
          member={editTarget}
          onClose={() => setEditTarget(null)}
          onResetLink={(res) => {
            setEditTarget(null);
            setInviteResult({ url: res.reset_url, expires: res.expires_at });
          }}
        />
      )}

      <HelpSection
        i18nKey="team.help"
        paragraphs={2}
        list={{
          titleKey: "team.help.list_title",
          itemsKey: "team.help.list_items",
          itemCount: 3,
        }}
      />
    </section>
  );
}

/* ---------------------------------------------------------------- MemberRow */

function MemberRow({
  member,
  onEdit,
}: {
  member: TeamMember;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useCurrentUser();
  const remove = useRemoveTeamMember();

  const isMe = me?.id === member.id;

  const memberStatusValue = !member.is_activated
    ? "pending"
    : member.is_active
      ? "active"
      : "disabled";

  return (
    <tr className="border-t border-border hover:bg-bg-secondary/50 transition-colors">
      <td className="px-4 py-3 font-medium">
        <div className="flex items-center gap-2">
          {member.username}
          {isMe && (
            <Badge tone="accent">{t("team.you")}</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-fg-muted">
        {t(`role.${member.role}`)}
      </td>
      <td className="px-4 py-3">
        <StatusBadge
          domain="team"
          value={memberStatusValue}
          label={t(`team.status.${memberStatusValue}`)}
          dot
        />
      </td>
      <td className="px-4 py-3 text-fg-muted text-xs">
        {member.last_login_at ? formatRelative(member.last_login_at) : "—"}
      </td>
      <td className="px-4 py-3 text-right space-x-2">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          {t("common.edit")}
        </Button>
        {!isMe && (
          <Button
            size="sm"
            variant="danger"
            loading={remove.isPending}
            onClick={async () => {
              const yes = await confirmAsync({
                title: t("team.remove_confirm", { username: member.username }),
                confirmKind: "danger",
                confirmLabel: t("team.remove"),
              });
              if (yes) void remove.mutateAsync(member.id);
            }}
          >
            {t("team.remove")}
          </Button>
        )}
      </td>
    </tr>
  );
}

/* --------------------------------------------------------- EditMemberDialog */

function EditMemberDialog({
  member,
  onClose,
  onResetLink,
}: {
  member: TeamMember;
  onClose: () => void;
  onResetLink: (res: ResetLinkResponse) => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useCurrentUser();
  const update = useUpdateTeamMember();
  const resetLink = useIssueResetLink();
  const reset2fa = useResetMember2fa();

  const isMe = me?.id === member.id;
  const [role, setRole] = useState<Role>(member.role);
  const [isActive, setIsActive] = useState(member.is_active);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: member.id,
        /* don't send role for self to prevent self-demotion */
        ...(isMe ? {} : { role }),
        is_active: isActive,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("team.update_error"));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {t("team.edit_title", { username: member.username })}
          </DialogTitle>
          <DialogDescription>
            {isMe
              ? t("team.edit_self_hint")
              : t("team.edit_description")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {/* Role selector — disabled for self (no self-demotion) */}
          <div className="space-y-2">
            <Label>{t("team.field.role")}</Label>
            {isMe ? (
              <p className="text-sm text-fg-muted">
                {t(`role.${member.role}`)}
                <span className="ml-2 text-xs">({t("team.cannot_change_own_role")})</span>
              </p>
            ) : (
              <SimpleSelect
                options={COMPANY_ROLES.map((r) => ({
                  value: r,
                  label: t(`role.${r}`),
                }))}
                value={role}
                onValueChange={(v) => setRole(v as Role)}
              />
            )}
          </div>

          {/* is_active toggle — Block/Unblock; hidden for pending (not activated) */}
          {member.is_activated && (
            <div className="rounded-lg border border-border p-4">
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={isMe}
                label={t("team.active_label")}
                description={
                  isMe
                    ? t("team.cannot_block_self")
                    : isActive
                      ? t("team.active_hint")
                      : t("team.inactive_hint")
                }
              />
            </div>
          )}

          {/* Recovery — the answer to "he opened the link and forgot the
              password" and "he changed the phone with the authenticator".
              There is no self-service reset: this product has no verified
              email to send one to, so it goes through the admin. */}
          {member.is_activated && (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div>
                <p className="text-sm font-medium text-fg-primary">
                  {t("team.recovery.title")}
                </p>
                <p className="text-xs text-fg-muted">{t("team.recovery.hint")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={resetLink.isPending}
                  onClick={async () => {
                    setError(null);
                    try {
                      onResetLink(await resetLink.mutateAsync(member.id));
                    } catch (err) {
                      setError(
                        err instanceof ApiError ? err.detail : t("team.recovery.link_error"),
                      );
                    }
                  }}
                >
                  {t("team.recovery.password_link")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={reset2fa.isPending}
                  onClick={async () => {
                    setError(null);
                    const yes = await confirmAsync({
                      title: t("team.recovery.confirm_2fa", { username: member.username }),
                      text: t("team.recovery.confirm_2fa_hint"),
                      confirmLabel: t("team.recovery.reset_2fa"),
                    });
                    if (!yes) return;
                    try {
                      await reset2fa.mutateAsync(member.id);
                      pushToast({ kind: "success", text: t("team.recovery.reset_2fa_done") });
                    } catch (err) {
                      setError(
                        err instanceof ApiError ? err.detail : t("team.recovery.reset_2fa_error"),
                      );
                    }
                  }}
                >
                  {t("team.recovery.reset_2fa")}
                </Button>
              </div>
            </div>
          )}

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={update.isPending} onClick={handleSave}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- InviteModal */

function InviteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (invite: { invite_url: string; invite_expires_at: string }) => void;
}) {
  const { t } = useTranslation();
  const invite = useInviteTeamMember();
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<Role>("company_operator");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await invite.mutateAsync({ username, role });
      onCreated({
        invite_url: result.invite_url,
        invite_expires_at: result.invite_expires_at,
      });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t("team.invite_error"));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("team.invite_title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite_username">{t("team.field.username")}</Label>
              <Input
                id="invite_username"
                required
                placeholder="alice"
                pattern="[A-Za-z0-9_-]+"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <p className="text-xs text-fg-muted">{t("team.field.username_hint")}</p>
            </div>

            <div className="space-y-2">
              <Label>{t("team.field.role")}</Label>
              <SimpleSelect
                options={COMPANY_ROLES.map((r) => ({
                  value: r,
                  label: t(`role.${r}`),
                }))}
                value={role}
                onValueChange={(v) => setRole(v as Role)}
              />
              <p className="text-xs text-fg-muted">{t(`team.role_hint.${role}`)}</p>
            </div>

            {error && <Alert tone="danger">{error}</Alert>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={invite.isPending}>
              {invite.isPending ? t("team.inviting") : t("team.create_invite")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------- InviteLinkModal */

function InviteLinkModal({
  url,
  expiresAt,
  onClose,
}: {
  url: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const fullUrl = `${window.location.origin}${url}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    pushToast({ kind: "success", text: t("team.invite_copied") });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("team.invite_ready")}</DialogTitle>
          <DialogDescription>{t("team.invite_subtitle")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <code className="block break-all rounded-lg bg-bg-elevated p-4 text-xs font-mono leading-relaxed border border-border">
            {fullUrl}
          </code>
          <p className="text-xs text-fg-muted">
            {t("team.invite_expires", {
              date: formatDateTime(expiresAt),
            })}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={handleCopy}>
            {copied ? t("team.invite_copied") : t("team.invite_copy")}
          </Button>
          <Button onClick={onClose}>{t("common.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
